import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Check, Star, Briefcase, Shield, MessageCircle } from 'lucide-react';
import axios from 'axios';

function getToken() {
    return localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
}

function initials(first, last) {
    return ((first?.[0] ?? '') + (last?.[0] ?? '')).toUpperCase() || '?';
}

function relativeDate(dt) {
    if (!dt) return '';
    const days = Math.floor((Date.now() - new Date(dt)) / 86400000);
    if (days === 0) return 'today';
    if (days < 7) return `${days}d ago`;
    if (days < 30) return `${Math.floor(days / 7)}w ago`;
    return `${Math.floor(days / 30)}mo ago`;
}

// ── Section wrapper ──────────────────────────────────────────────────────────
function CpSection({ title, sub, children }) {
    return (
        <section className="cp-section">
            <header className="cp-section-head">
                <h2 className="cp-section-title">{title}</h2>
                {sub && <p className="cp-section-sub">{sub}</p>}
            </header>
            {children}
        </section>
    );
}

// ── Verification row ─────────────────────────────────────────────────────────
function VRow({ ok, icon: Icon, label, sub }) {
    return (
        <li className={`cp-verify-row${ok ? ' is-ok' : ''}`}>
            <span className="cp-verify-icon"><Icon size={13} /></span>
            <div className="cp-verify-text">
                <span className="cp-verify-l">{label}</span>
                {sub && <span className="cp-verify-sub">{sub}</span>}
            </div>
            <span className="cp-verify-r">
                {ok ? <><Check size={11} /> Verified</> : 'Not provided'}
            </span>
        </li>
    );
}

// ── Main modal component ─────────────────────────────────────────────────────
const CandidateDetails = ({ application, job, onClose, onAccept, onReject }) => {
    const user = application?.user;
    const [stats, setStats]           = useState(null);
    const [pastJobs, setPastJobs]     = useState([]);
    const [reviewsShown, setReviewsShown] = useState(5);
    const [pastShown, setPastShown]   = useState(5);
    const [actionLoading, setActionLoading] = useState(false);
    const [toast, setToast]           = useState('');
    const navigate = useNavigate();

    const ini = initials(user?.firstname, user?.lastname);
    const name = `${user?.firstname ?? ''} ${user?.lastname ?? ''}`.trim() || user?.username || 'Unknown';
    const isIdVerified    = !!user?.passportUrl;
    const isPhoneVerified = !!(user?.phoneNumber && user.phoneNumber !== '+44');
    const isEmailVerified = !!user?.email;

    // Lock scroll
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, []);

    // ESC to close
    useEffect(() => {
        const fn = e => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', fn);
        return () => document.removeEventListener('keydown', fn);
    }, [onClose]);

    // Fetch user stats
    useEffect(() => {
        if (!user?.id) return;
        const token = getToken();
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users/${user.id}/stats`, {
            headers: { Authorization: `Bearer ${token}` },
        }).then(r => setStats(r.data)).catch(() => {});
    }, [user?.id]);

    // Fetch past completed jobs for this candidate
    useEffect(() => {
        if (!user?.id) return;
        const token = getToken();
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users/${user.id}/applied`, {
            headers: { Authorization: `Bearer ${token}` },
        }).then(r => {
            const completed = (r.data ?? []).filter(a =>
                a.status === 'ACCEPTED' && a.job?.jobStatus === 'COMPLETED'
            );
            setPastJobs(completed);
        }).catch(() => {});
    }, [user?.id]);

    const flash = msg => { setToast(msg); setTimeout(() => setToast(''), 3000); };

    // Poster → applicant: open (or create) the job-scoped conversation, then
    // jump to the inbox with it selected.
    const startChat = async () => {
        if (!job?.id || !user?.id) return;
        try {
            const r = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/chat/conversations/start`,
                { jobId: job.id, workerId: user.id },
                { headers: { Authorization: `Bearer ${getToken()}` } }
            );
            navigate(`/messages?c=${r.data.id}`);
        } catch {
            flash('Could not open chat.');
        }
    };

    const handleAccept = async () => {
        setActionLoading(true);
        try {
            await onAccept(application.id);
            flash('Application accepted!');
        } catch { flash('Failed to accept.'); }
        finally { setActionLoading(false); }
    };

    const handleReject = async () => {
        setActionLoading(true);
        try {
            await onReject(application.id);
            flash('Application declined.');
        } catch { flash('Failed to decline.'); }
        finally { setActionLoading(false); }
    };

    const rating   = stats?.rating ?? 4.9;
    const jobsDone = stats?.jobsDone ?? 0;
    const appliedAgo = relativeDate(application?.applicationDate);

    const visiblePast = pastJobs.slice(0, pastShown);

    if (!user) return null;

    return (
        <div className="cp-scrim" onClick={onClose}>
            <div className="cp-modal" role="dialog" aria-modal="true" onClick={e => e.stopPropagation()}>

                {/* Top bar */}
                <div className="cp-topbar">
                    <button className="cp-back" onClick={onClose}>
                        <span className="cp-back-arrow">←</span> Back to candidates
                    </button>
                    <div className="cp-topbar-r">
                        <span className="mono" style={{ color: 'var(--ink-500)', fontSize: 12 }}>
                            Applied {appliedAgo} for
                        </span>
                        <span className="cp-job-pill">{job?.title ?? 'this job'}</span>
                        <button
                            style={{ width: 30, height: 30, border: 'none', borderRadius: 'var(--r-sm)', background: 'var(--ink-100)', cursor: 'pointer', display: 'grid', placeItems: 'center', color: 'var(--ink-700)' }}
                            onClick={onClose} aria-label="Close">
                            <X size={16} />
                        </button>
                    </div>
                </div>

                {/* Scrollable body */}
                <div className="cp-body">

                    {/* Hero */}
                    <header className="cp-hero">
                        <div className="cp-hero-bg" />
                        <div className="cp-hero-row">
                            <div className="cp-avatar">{ini}</div>
                            <div className="cp-id">
                                <div className="cp-name-line">
                                    <h1 className="cp-name">{name}</h1>
                                    {isIdVerified && (
                                        <span className="cp-verified">
                                            <Shield size={11} /> ID verified
                                        </span>
                                    )}
                                </div>
                                <div className="cp-headline">
                                    <span className="cp-headline-rating">
                                        <Star size={13} style={{ color: 'var(--amber)' }} />
                                        {rating.toFixed(1)}{' '}
                                        <span className="cp-headline-rating-sub">({jobsDone} job{jobsDone !== 1 ? 's' : ''})</span>
                                    </span>
                                    <span className="cp-dot" />
                                    <span><Briefcase size={12} /> {jobsDone} done on Interim</span>
                                </div>
                            </div>
                            <div className="cp-hero-actions">
                                <button
                                    className="ix-btn ix-btn-outline ix-btn-sm"
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                                    onClick={startChat}
                                >
                                    <MessageCircle size={13} /> Message
                                </button>
                            </div>
                        </div>
                    </header>

                    {/* Two-column grid */}
                    <div className="cp-grid">
                        <div className="cp-main">

                            {/* Bio / about */}
                            {user.bio ? (
                                <CpSection title="About">
                                    <p className="cp-bio">{user.bio}</p>
                                </CpSection>
                            ) : (
                                <CpSection title="About">
                                    <p className="cp-bio" style={{ color: 'var(--ink-400)', fontStyle: 'italic' }}>
                                        This candidate hasn't added a bio yet.
                                    </p>
                                </CpSection>
                            )}

                            {/* Location */}
                            {user.location && (
                                <CpSection title="Location">
                                    <p className="cp-bio">{user.location}</p>
                                </CpSection>
                            )}

                            {/* Past completed jobs on Interim */}
                            <CpSection
                                title={`Past jobs on Interim (${pastJobs.length})`}
                                sub="Completed jobs they've done through the platform."
                            >
                                {pastJobs.length === 0 ? (
                                    <div className="cp-reviews-empty">
                                        No completed jobs yet — this would be one of their first on Interim.
                                    </div>
                                ) : (
                                    <>
                                        <ul className="cp-pastjobs">
                                            {visiblePast.map((a, i) => (
                                                <li key={i} className="cp-pastjob">
                                                    <span className="cp-pastjob-icon"><Briefcase size={14} /></span>
                                                    <span className="cp-pastjob-meta">
                                                        <span className="cp-pastjob-role">{a.job?.title}</span>
                                                        <span className="cp-pastjob-sub">
                                                            {a.job?.address} · {relativeDate(a.job?.dateTime)}
                                                        </span>
                                                    </span>
                                                    <span className="cp-pastjob-pay">€{a.job?.payment?.toFixed(0)}</span>
                                                </li>
                                            ))}
                                        </ul>
                                        {pastJobs.length > pastShown && (
                                            <div className="cp-reviews-more-wrap" style={{ marginTop: 10 }}>
                                                <button className="cp-reviews-more" onClick={() => setPastShown(n => n + 10)}>
                                                    Show more <span className="mono">({pastJobs.length - pastShown} left)</span>
                                                </button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </CpSection>

                            {/* No review system yet */}
                            <CpSection title="Reviews from posters">
                                <div className="cp-reviews-empty">
                                    The review system is coming soon. For now, check their completed jobs above.
                                </div>
                            </CpSection>

                        </div>

                        {/* Sidebar */}
                        <aside className="cp-side">
                            <div className="cp-side-card">
                                <h3 className="cp-side-title">Verification</h3>
                                <ul className="cp-verify">
                                    <VRow ok={isIdVerified}    icon={Shield} label="ID verified"
                                          sub={isIdVerified ? 'Photo ID checked' : 'Not yet submitted'} />
                                    <VRow ok={isPhoneVerified} icon={Check}  label="Phone"
                                          sub={isPhoneVerified ? user.phoneNumber : 'Not provided'} />
                                    <VRow ok={isEmailVerified} icon={Check}  label="Email"
                                          sub={user.email ?? 'Not provided'} />
                                </ul>
                            </div>

                            {stats && (
                                <div className="cp-side-card">
                                    <h3 className="cp-side-title">Stats</h3>
                                    <ul className="cp-verify">
                                        <li className="cp-verify-row is-ok">
                                            <span className="cp-verify-icon"><Star size={13} /></span>
                                            <div className="cp-verify-text">
                                                <span className="cp-verify-l">Rating</span>
                                            </div>
                                            <span className="cp-verify-r">{rating.toFixed(1)} ★</span>
                                        </li>
                                        <li className="cp-verify-row is-ok">
                                            <span className="cp-verify-icon"><Briefcase size={13} /></span>
                                            <div className="cp-verify-text">
                                                <span className="cp-verify-l">Jobs completed</span>
                                            </div>
                                            <span className="cp-verify-r">{jobsDone}</span>
                                        </li>
                                        <li className="cp-verify-row is-ok">
                                            <span className="cp-verify-icon"><Check size={13} /></span>
                                            <div className="cp-verify-text">
                                                <span className="cp-verify-l">Response time</span>
                                            </div>
                                            <span className="cp-verify-r">{stats.responseTime ?? '< 1 hr'}</span>
                                        </li>
                                    </ul>
                                </div>
                            )}

                            <button className="cp-report">Report this profile</button>
                        </aside>
                    </div>
                </div>

                {/* Sticky action bar */}
                <div className="cp-actionbar">
                    <div className="cp-actionbar-l">
                        <span className="ml-cand-avatar">{ini}</span>
                        <div>
                            <div className="cp-actionbar-name">{name}</div>
                            <div className="cp-actionbar-sub">
                                ★ {rating.toFixed(1)} · {jobsDone} jobs · Applied {appliedAgo}
                            </div>
                        </div>
                    </div>
                    <div className="cp-actionbar-r">
                        {application.status === 'PENDING' && (
                            <>
                                <button
                                    className="ix-btn ix-btn-outline"
                                    onClick={handleReject}
                                    disabled={actionLoading}
                                >
                                    Pass
                                </button>
                                <button
                                    className="ix-btn ix-btn-outline"
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                                    onClick={startChat}
                                >
                                    <MessageCircle size={14} /> Message
                                </button>
                                <button
                                    className="ix-btn ix-btn-primary ix-btn-lg"
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                                    onClick={handleAccept}
                                    disabled={actionLoading}
                                >
                                    <Check size={14} /> Accept &amp; schedule
                                </button>
                            </>
                        )}
                        {application.status === 'ACCEPTED' && (
                            <span className="ml-badge ml-badge-active" style={{ padding: '8px 16px', fontSize: 13 }}>
                                <Check size={13} /> Accepted
                            </span>
                        )}
                        {application.status === 'REJECTED' && (
                            <span className="ml-badge ml-badge-cancelled" style={{ padding: '8px 16px', fontSize: 13 }}>
                                Declined
                            </span>
                        )}
                    </div>
                </div>

                {toast && <div className="ml-toast">{toast}</div>}
            </div>
        </div>
    );
};

export default CandidateDetails;
